/**
 * AURA v2 TopBar — rewritten Apr 22, 2026 (Phase 2 UI rebuild).
 *
 * Lanes, left → right:
 *   logo · tenant switcher · ⌘K search · (admin-only) dept-dropdown ·
 *   role chip · density toggle · theme toggle · credits chip · AI (⌘J) ·
 *   notifications bell · user avatar menu
 *
 * No backend changes. Tenant switcher is a read-only display for now
 * (backend tenant-list endpoint is /admin/* — super-admin only). It falls
 * back to a single-tenant display for regular users.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Command, Bell, Sparkles, Sun, Moon, LogOut, User as UserIcon,
  ChevronDown, Check, Rows3, Rows2, Menu, CalendarDays, Clock, LifeBuoy,
} from 'lucide-react';
import { useAuthStore } from '../../stores/auth';
import { useTheme } from '../../contexts/ThemeContext';
import { resolveRoleKey } from '../../pages/dashboard/roleLayouts';
import { SIDEBAR_VIEWS, SIDEBAR_VIEW_GROUPS, type SidebarView } from './Sidebar';
import { api } from '../../utils/api';
import { CallAvailabilityChip } from './CallAvailabilityChip';

/* ------------------------------------------------------------------ */
/*  Notifications (2026-06-09): real data from /api/v1/notifications,   */
/*  polled (no realtime infra yet). Same feed the mobile app reads.     */
/* ------------------------------------------------------------------ */
interface NotifItem {
  id: string;
  title: string;
  body?: string | null;
  type: string;
  link?: string | null;
  isRead: boolean;
  createdAt: string;
}

function timeAgo(iso: string): string {
  const t = new Date(iso).getTime();
  if (!t || Number.isNaN(t)) return '';
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60) return 'now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

function notifColor(type: string): string {
  const t = (type || '').toLowerCase();
  if (t.includes('won') || t.includes('approv')) return 'var(--forest)';
  if (t.includes('reject') || t.includes('sla') || t.includes('breach')) return 'var(--wine)';
  if (t.includes('discount') || t.includes('hot') || t.includes('assign')) return 'var(--saffron)';
  return 'var(--info)';
}

interface TopBarProps {
  onCmdOpen: () => void;
  onAiOpen: () => void;
  density: 'compact' | 'comfortable';
  onDensityChange: (d: 'compact' | 'comfortable') => void;
  viewOverride: SidebarView | null;
  onViewOverrideChange: (v: SidebarView | null) => void;
  /** True when viewport is <=768px — shows hamburger, hides desktop controls */
  isMobile?: boolean;
  /** Hamburger tap handler — opens mobile sidebar drawer */
  onHamburger?: () => void;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                             */
/* ------------------------------------------------------------------ */

function tenantInitial(name?: string) {
  if (!name) return 'A';
  return name.trim()[0]?.toUpperCase() ?? 'A';
}

/* Deterministic warm-palette accent so two tenants in a row don't collide. */
function tenantAccent(slug: string | undefined | null): string {
  if (!slug) return 'var(--saffron-600)';
  const palette = ['#C98A3B', '#2F7A5B', '#3A6A8C', '#8B4A6B', '#6B5B8C', '#B87A2E'];
  let h = 0;
  for (let i = 0; i < slug.length; i++) h = (h * 31 + slug.charCodeAt(i)) | 0;
  return palette[Math.abs(h) % palette.length];
}

/* ------------------------------------------------------------------ */
/*  Component                                                           */
/* ------------------------------------------------------------------ */

export function TopBar({
  onCmdOpen,
  onAiOpen,
  density,
  onDensityChange,
  viewOverride,
  onViewOverrideChange,
  isMobile = false,
  onHamburger,
}: TopBarProps) {
  const navigate = useNavigate();

  // Notifications: initial load + 30s poll (no realtime infra yet — same feed
  // the mobile app reads from /api/v1/notifications).
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const [list, cnt] = await Promise.all([
          api.get('/notifications', { params: { limit: 15 } }),
          api.get('/notifications/unread-count'),
        ]);
        if (!alive) return;
        setNotifs((list.data?.data as NotifItem[]) ?? []);
        setUnreadCount(cnt.data?.data?.count ?? 0);
      } catch {
        /* offline — keep last state */
      }
    };
    load();
    const t = setInterval(load, 30000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  const markNotifRead = (n: NotifItem) => {
    if (!n.isRead) {
      setNotifs((prev) => prev.map((x) => (x.id === n.id ? { ...x, isRead: true } : x)));
      setUnreadCount((c) => Math.max(0, c - 1));
      api.patch(`/notifications/${n.id}/read`).catch(() => {});
    }
    if (n.link) {
      setNotifOpen(false);
      navigate(n.link);
    }
  };

  const markAllNotifsRead = () => {
    setNotifs((prev) => prev.map((x) => ({ ...x, isRead: true })));
    setUnreadCount(0);
    api.patch('/notifications/read-all').catch(() => {});
  };
  const { user, tenant, logout } = useAuthStore();
  const { resolvedTheme, toggleTheme } = useTheme();

  const [tenantOpen, setTenantOpen] = useState(false);
  const [roleOpen, setRoleOpen] = useState(false);
  const [deptOpen, setDeptOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifs, setNotifs] = useState<NotifItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const [credits, setCredits] = useState<number | null>(null);
  const [creditsLoading, setCreditsLoading] = useState(true);

  // Refs for outside-click detection
  const rootRef = useRef<HTMLDivElement | null>(null);

  const roleName = (user?.role?.name ?? '').toLowerCase();
  const isAdmin = roleName === 'admin';
  // May 26, 2026 — strict-view roles hide the global search (no perms to
  // read contacts/deals/invoices anyway; UI mirrors backend gating).
  const isStrictView = roleName === 'lead_triage_coordinator';
  const isSuperAdmin =
    user?.email === 'admin@aura.ardhanex.com' || user?.email === 'admin@aura.ardhanex.info';

  // Fetch credits once on mount + whenever tenant changes.
  // Apr 23, 2026 (Bug 7) — also listen for `aura:credits:refresh` custom events so
  // the chip updates after any AI feature deducts credits (see utils/credits-bus.ts).
  useEffect(() => {
    let active = true;
    const fetchBalance = () => {
      setCreditsLoading(true);
      api.get('/credits/balance')
        .then((res) => {
          if (!active) return;
          const bal = res?.data?.data?.balance;
          setCredits(typeof bal === 'number' ? bal : null);
        })
        .catch(() => { if (active) setCredits(null); })
        .finally(() => { if (active) setCreditsLoading(false); });
    };
    fetchBalance();
    const onRefresh = () => fetchBalance();
    window.addEventListener('aura:credits:refresh', onRefresh);
    return () => {
      active = false;
      window.removeEventListener('aura:credits:refresh', onRefresh);
    };
  }, [tenant?.slug]);

  // Outside-click closes any open popover
  useEffect(() => {
    if (!tenantOpen && !roleOpen && !deptOpen && !notifOpen && !userMenuOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target as Node)) {
        setTenantOpen(false);
        setRoleOpen(false);
        setDeptOpen(false);
        setNotifOpen(false);
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [tenantOpen, roleOpen, deptOpen, notifOpen, userMenuOpen]);

  const initials = `${user?.firstName?.[0] ?? ''}${user?.lastName?.[0] ?? ''}`.toUpperCase() || 'U';
  const displayName = user ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() : 'User';
  const roleLabel = user?.role?.displayName ?? user?.role?.name ?? '—';
  const roleKey = resolveRoleKey(user as never);
  const accent = tenantAccent(tenant?.slug);
  // "My HR" self-service lives in the user menu for Veda tenants (null modules = admin/all).
  const vedaHr = !tenant?.enabledModules || tenant.enabledModules.includes('veda_commodity_deals');

  return (
    <div
      ref={rootRef}
      className="relative flex items-center gap-2 px-4"
      style={{
        height: 52,
        background: 'var(--ivory-1)',
        borderBottom: '1px solid var(--line)',
        color: 'var(--ink-1)',
        zIndex: 30,
        flexShrink: 0,
      }}
    >
      {/* Hamburger — mobile only (md:hidden via isMobile prop check) */}
      {isMobile && (
        <button
          type="button"
          onClick={onHamburger}
          aria-label="Open navigation"
          className="flex items-center justify-center rounded-md"
          style={{ width: 34, height: 34, color: 'var(--ink-2)', flexShrink: 0 }}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--ivory-2)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
        >
          <Menu size={18} />
        </button>
      )}

      {/* Logo */}
      <div className="flex items-center gap-2.5" style={{ width: isMobile ? 'auto' : 180, flexShrink: 0 }}>
        <div
          className="flex items-center justify-center"
          style={{
            width: 28, height: 28, borderRadius: 7,
            background: 'linear-gradient(135deg, #1B1713, #3A332B)',
            color: '#FEFCF7',
            fontFamily: 'var(--ff-serif)', fontWeight: 600, fontSize: 15,
            letterSpacing: -0.5,
          }}
        >A</div>
        <div style={{ fontFamily: 'var(--ff-serif)', fontWeight: 600, fontSize: 17, color: 'var(--ink-0)' }}>
          Aura
        </div>
      </div>

      {/* Tenant switcher — desktop only */}
      {!isMobile && (
        <div className="relative">
          <button
            type="button"
            onClick={() => {
              setTenantOpen((o) => !o);
              setRoleOpen(false); setDeptOpen(false); setNotifOpen(false); setUserMenuOpen(false);
            }}
            className="flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-[13px]"
            style={{
              minWidth: 200, maxWidth: 240,
              background: 'var(--ivory-0)',
              borderColor: 'var(--line)',
              color: 'var(--ink-0)',
            }}
          >
            <div
              className="flex items-center justify-center rounded font-semibold"
              style={{
                width: 18, height: 18, fontSize: 11,
                background: accent, color: '#fff',
                flexShrink: 0,
              }}
            >
              {tenantInitial(tenant?.name)}
            </div>
            <span className="flex-1 truncate text-left" style={{ fontWeight: 500 }}>
              {tenant?.name ?? 'No organisation'}
            </span>
            <ChevronDown size={13} style={{ opacity: 0.5 }} />
          </button>
          {tenantOpen && (
            <Dropdown width={280}>
              <DropdownLabel>Current organisation</DropdownLabel>
              <DropdownItem>
                <div
                  className="flex items-center justify-center rounded font-semibold"
                  style={{ width: 22, height: 22, fontSize: 12, background: accent, color: '#fff', flexShrink: 0 }}
                >
                  {tenantInitial(tenant?.name)}
                </div>
                <div className="flex-1">
                  <div style={{ fontWeight: 500 }}>{tenant?.name ?? '—'}</div>
                  <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                    {tenant?.slug ?? ''}
                  </div>
                </div>
                <Check size={14} style={{ color: 'var(--forest-600)' }} />
              </DropdownItem>
              {isSuperAdmin && (
                <>
                  <DropdownDivider />
                  <DropdownItem onClick={() => { navigate('/admin/tenants'); setTenantOpen(false); }}>
                    <span style={{ color: 'var(--ink-3)' }}>Manage tenants…</span>
                  </DropdownItem>
                </>
              )}
            </Dropdown>
          )}
        </div>
      )}

      {/* Search / ⌘K — desktop only; hidden for strict-view roles (no read perms) */}
      {!isMobile && !isStrictView && (
        <button
          type="button"
          onClick={onCmdOpen}
          className="flex items-center gap-2 rounded-md border text-[13px]"
          style={{
            flex: 1, maxWidth: 420,
            height: 32, padding: '0 10px',
            background: 'var(--ivory-2)',
            borderColor: 'var(--line)',
            color: 'var(--ink-3)',
            cursor: 'pointer',
          }}
        >
          <Search size={14} />
          <span className="flex-1 text-left truncate">Search contacts, deals, invoices…</span>
          <span
            className="rounded border px-1.5 py-0.5 text-[11px] font-mono"
            style={{
              background: 'var(--ivory-1)',
              borderColor: 'var(--line)',
              color: 'var(--ink-3)',
            }}
          >⌘K</span>
        </button>
      )}

      <div className="flex-1" />

      {/* Admin-only dept dropdown — desktop only */}
      {!isMobile && (isAdmin || isSuperAdmin) && (
        <div className="relative">
          <button
            type="button"
            onClick={() => {
              setDeptOpen((o) => !o);
              setTenantOpen(false); setRoleOpen(false); setNotifOpen(false); setUserMenuOpen(false);
            }}
            className="flex items-center gap-1.5 rounded-md border px-2 py-1 text-[12px]"
            style={{
              background: viewOverride ? 'var(--saffron-50)' : 'var(--ivory-0)',
              borderColor: viewOverride ? 'var(--saffron-100)' : 'var(--line)',
              color: viewOverride ? 'var(--saffron-600)' : 'var(--ink-2)',
            }}
            title="View sidebar as another role"
          >
            <span>{viewOverride ? `View: ${labelForView(viewOverride)}` : 'View as…'}</span>
            <ChevronDown size={11} style={{ opacity: 0.5 }} />
          </button>
          {deptOpen && (
            <Dropdown width={240} align="right">
              <DropdownLabel>Browse sidebar as</DropdownLabel>
              <DropdownItem
                onClick={() => { onViewOverrideChange(null); setDeptOpen(false); }}
              >
                <span className="flex-1">My role ({roleLabel})</span>
                {!viewOverride && <Check size={14} style={{ color: 'var(--forest-600)' }} />}
              </DropdownItem>
              {/* Grouped: Managers / Executives (Apr 22, 2026 exec sprint) */}
              {SIDEBAR_VIEW_GROUPS.map((group, gIdx) => (
                <div key={group.heading}>
                  {gIdx === 0 ? <DropdownDivider /> : <DropdownDivider />}
                  <DropdownLabel>{group.heading}</DropdownLabel>
                  {group.views.map((v) => (
                    <DropdownItem
                      key={v.id}
                      onClick={() => { onViewOverrideChange(v.id); setDeptOpen(false); }}
                    >
                      <span className="flex-1">{v.label}</span>
                      {viewOverride === v.id && <Check size={14} style={{ color: 'var(--forest-600)' }} />}
                    </DropdownItem>
                  ))}
                </div>
              ))}
            </Dropdown>
          )}
        </div>
      )}

      {/* Role chip — desktop only */}
      {!isMobile && (
        <div className="relative">
          <button
            type="button"
            onClick={() => {
              setRoleOpen((o) => !o);
              setTenantOpen(false); setDeptOpen(false); setNotifOpen(false); setUserMenuOpen(false);
            }}
            className="flex items-center gap-1.5 rounded-md border px-2 py-1 text-[12px]"
            style={{
              background: 'var(--ivory-0)',
              borderColor: 'var(--line)',
              color: 'var(--ink-2)',
            }}
            title="Role info"
          >
            <span>{roleLabel}</span>
            <ChevronDown size={11} style={{ opacity: 0.5 }} />
          </button>
          {roleOpen && (
            <Dropdown width={240} align="right">
              <DropdownLabel>Signed in as</DropdownLabel>
              <div className="px-2.5 py-2" style={{ fontSize: 12, color: 'var(--ink-2)' }}>
                <div style={{ fontWeight: 500, color: 'var(--ink-0)' }}>{displayName}</div>
                <div>{user?.email}</div>
                <div className="mt-1" style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                  Role: {roleLabel}
                </div>
                <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                  Key: {roleKey}
                </div>
                {user?.department?.name && (
                  <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                    Department: {user.department.name}
                  </div>
                )}
              </div>
            </Dropdown>
          )}
        </div>
      )}

      {/* Call Availability chip — desktop only; role-gated inside the component */}
      {!isMobile && <CallAvailabilityChip />}

      {/* Density toggle — desktop only */}
      {!isMobile && (
        <IconButton
          onClick={() => onDensityChange(density === 'compact' ? 'comfortable' : 'compact')}
          title={density === 'compact' ? 'Switch to comfortable' : 'Switch to compact'}
        >
          {density === 'compact' ? <Rows3 size={14} /> : <Rows2 size={14} />}
        </IconButton>
      )}

      {/* Theme toggle */}
      <IconButton
        onClick={toggleTheme}
        title={resolvedTheme === 'dark' ? 'Light mode' : 'Dark mode'}
      >
        {resolvedTheme === 'dark' ? <Sun size={14} /> : <Moon size={14} />}
      </IconButton>

      {/* Credits chip — desktop only */}
      {!isMobile && (
        <button
          type="button"
          onClick={() => navigate('/credits')}
          className="flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[12px]"
          style={{
            background: 'linear-gradient(180deg, var(--saffron-50) 0%, var(--saffron-100) 100%)',
            borderColor: 'var(--saffron-100)',
            color: 'var(--saffron-600)',
            fontWeight: 500,
          }}
          title="AI credits"
        >
          <Sparkles size={13} />
          <span className="tabular-nums">
            {creditsLoading ? '…' : credits !== null ? credits.toLocaleString('en-IN') : '—'}
          </span>
          <span style={{ opacity: 0.75, fontWeight: 400 }}>credits</span>
        </button>
      )}

      {/* AI assistant (⌘J) — desktop only */}
      {!isMobile && (
        <button
          type="button"
          onClick={onAiOpen}
          className="flex items-center gap-1.5 rounded-md px-2 py-1 text-[12px]"
          style={{ color: 'var(--ink-2)', background: 'transparent' }}
          title="AI Assistant · ⌘J"
          onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--ivory-2)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
        >
          <Command size={13} />
          <span className="font-mono" style={{ fontSize: 10, color: 'var(--ink-3)' }}>⌘J</span>
        </button>
      )}

      {/* Notifications */}
      <div className="relative">
        <button
          type="button"
          onClick={() => {
            setNotifOpen((o) => !o);
            setTenantOpen(false); setRoleOpen(false); setDeptOpen(false); setUserMenuOpen(false);
          }}
          className="relative flex items-center justify-center rounded-md"
          style={{ width: 30, height: 30, color: 'var(--ink-2)', background: 'transparent' }}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--ivory-2)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
        >
          <Bell size={15} />
          {unreadCount > 0 && (
            <span
              className="absolute flex items-center justify-center"
              style={{
                top: 1, right: -1, minWidth: 14, height: 14, padding: '0 3px',
                borderRadius: 7, background: 'var(--saffron)', color: '#fff',
                fontSize: 9, fontWeight: 700, lineHeight: 1,
              }}
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
        {notifOpen && (
          <Dropdown width={340} align="right" padding={0}>
            <div
              className="flex items-center justify-between border-b px-3.5 py-2.5"
              style={{ borderColor: 'var(--line)' }}
            >
              <span className="text-[13px] font-medium" style={{ color: 'var(--ink-0)' }}>
                Notifications
              </span>
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={markAllNotifsRead}
                  className="text-[11px] font-medium"
                  style={{ color: 'var(--saffron)', background: 'transparent' }}
                >
                  Mark all read
                </button>
              )}
            </div>
            <div style={{ maxHeight: 360, overflowY: 'auto' }}>
              {notifs.length === 0 ? (
                <div
                  className="px-3.5 py-6 text-center text-[12px]"
                  style={{ color: 'var(--ink-4)' }}
                >
                  You&apos;re all caught up.
                </div>
              ) : (
                notifs.map((n) => (
                  <NotificationRow
                    key={n.id}
                    title={n.title}
                    body={n.body ?? ''}
                    ago={timeAgo(n.createdAt)}
                    color={notifColor(n.type)}
                    unread={!n.isRead}
                    onClick={() => markNotifRead(n)}
                  />
                ))
              )}
            </div>
          </Dropdown>
        )}
      </div>

      {/* User avatar */}
      <div className="relative">
        <button
          type="button"
          onClick={() => {
            setUserMenuOpen((o) => !o);
            setTenantOpen(false); setRoleOpen(false); setDeptOpen(false); setNotifOpen(false);
          }}
          className="flex items-center justify-center rounded-full font-semibold"
          style={{
            width: 30, height: 30,
            background: accent,
            color: '#fff', fontSize: 11,
          }}
          title={displayName}
        >
          {initials || 'U'}
        </button>
        {userMenuOpen && (
          <Dropdown width={220} align="right">
            <div className="px-2.5 py-2" style={{ fontSize: 12 }}>
              <div style={{ fontWeight: 500, color: 'var(--ink-0)' }}>{displayName}</div>
              <div style={{ color: 'var(--ink-3)' }}>{user?.email}</div>
            </div>
            <DropdownDivider />
            <DropdownItem onClick={() => { navigate('/profile'); setUserMenuOpen(false); }}>
              <UserIcon size={14} />
              <span className="flex-1">Profile</span>
            </DropdownItem>
            {!isStrictView && (
              <DropdownItem onClick={() => { navigate('/settings'); setUserMenuOpen(false); }}>
                <span className="flex-1" style={{ marginLeft: 22 }}>Settings</span>
              </DropdownItem>
            )}
            {vedaHr && (
              <>
                <DropdownDivider />
                <div className="px-2.5 py-1" style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ink-3)' }}>My HR</div>
                <DropdownItem onClick={() => { navigate('/veda-agro/leaves'); setUserMenuOpen(false); }}>
                  <CalendarDays size={14} />
                  <span className="flex-1">Leaves</span>
                </DropdownItem>
                <DropdownItem onClick={() => { navigate('/veda-agro/my-attendance'); setUserMenuOpen(false); }}>
                  <Clock size={14} />
                  <span className="flex-1">My Attendance</span>
                </DropdownItem>
              </>
            )}
            {/* 🔒 Tour bug 8 (2026-10-07): the Resolution Centre takes a report from
                ANY logged-in user, but its only sidebar item is admin-only. This
                menu is the one door every role has, whatever sidebar it gets. */}
            {vedaHr && (
              <DropdownItem onClick={() => { navigate('/veda-agro/resolution-centre'); setUserMenuOpen(false); }}>
                <LifeBuoy size={14} />
                <span className="flex-1">Report a bug</span>
              </DropdownItem>
            )}
            <DropdownDivider />
            <DropdownItem
              onClick={async () => { await logout(); setUserMenuOpen(false); navigate('/login'); }}
              danger
            >
              <LogOut size={14} />
              <span className="flex-1">Logout</span>
            </DropdownItem>
          </Dropdown>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Primitives (scoped to TopBar)                                       */
/* ------------------------------------------------------------------ */

function IconButton({
  onClick, title, children,
}: { onClick: () => void; title: string; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className="flex items-center justify-center rounded-md"
      style={{ width: 30, height: 30, color: 'var(--ink-2)', background: 'transparent' }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--ivory-2)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
    >
      {children}
    </button>
  );
}

function Dropdown({
  children, width = 240, align = 'left', padding = 4,
}: {
  children: React.ReactNode; width?: number; align?: 'left' | 'right'; padding?: number;
}) {
  return (
    <div
      className="absolute rounded-lg border shadow-lg"
      style={{
        top: '100%',
        [align]: 0,
        marginTop: 4,
        width,
        padding,
        background: 'var(--ivory-1)',
        borderColor: 'var(--line)',
        boxShadow: 'var(--sh-3)',
        zIndex: 50,
      } as React.CSSProperties}
    >
      {children}
    </div>
  );
}

function DropdownLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wider"
      style={{ color: 'var(--ink-3)' }}
    >
      {children}
    </div>
  );
}

function DropdownItem({
  onClick, children, danger = false,
}: { onClick?: () => void; children: React.ReactNode; danger?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-[13px]"
      style={{
        color: danger ? 'var(--danger)' : 'var(--ink-1)',
        background: 'transparent',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--ivory-2)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
    >
      {children}
    </button>
  );
}

function DropdownDivider() {
  return <div className="my-1 h-px" style={{ background: 'var(--line)' }} />;
}

function NotificationRow({
  title, body, ago, color, unread, onClick,
}: { title: string; body: string; ago: string; color: string; unread?: boolean; onClick?: () => void }) {
  return (
    <div
      onClick={onClick}
      className="flex gap-2.5 border-b px-3.5 py-2.5"
      style={{
        borderColor: 'var(--ivory-3)',
        cursor: onClick ? 'pointer' : 'default',
        background: unread ? 'var(--ivory-2)' : 'transparent',
      }}
    >
      <span
        className="mt-1.5 rounded-full"
        style={{ width: 6, height: 6, background: unread ? color : 'var(--line)', flexShrink: 0 }}
      />
      <div className="flex-1">
        <div
          className="text-[13px]"
          style={{ color: 'var(--ink-0)', fontWeight: unread ? 700 : 500 }}
        >
          {title}
        </div>
        <div className="text-[12px]" style={{ color: 'var(--ink-3)' }}>{body}</div>
      </div>
      {ago && (
        <div className="text-[11px]" style={{ color: 'var(--ink-4)' }}>{ago}</div>
      )}
    </div>
  );
}

function labelForView(v: SidebarView): string {
  const found = SIDEBAR_VIEWS.find((x) => x.id === v);
  return found?.label ?? v;
}
